/**
 * MemberJourneyCard: the member profile's Journey card (DA, E1 to E5 badges).
 *
 * GET api/callcentre/journey/members/{memberId} (Member.Read OR CallCentre.Read). Completed steps show as a
 * coloured badge with the year (and the event or course on hover); steps not done yet are greyed out. Entries sent
 * to Planning Centre (Stage 4) show Sent, Waiting or Not linked to Planning Centre under the badges.
 * Shows only for people with Member.Read or CallCentre.Read (or a call centre console role), and hides itself
 * quietly when the call fails (403, 404 or the service being down): it must never break the member page.
 * People with CallCentre.Journey.Manage get "Edit", which opens the record form for this member.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { IconPencil, IconRoute } from '@tabler/icons-react';
import { Card, Group, Stack, Text, Button, ThemeIcon, Skeleton } from '@/components/ui';
import { useCallCentreAdminService } from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { useCallCentrePermissions } from '@/hooks/callcentre/useCallCentrePermissions';
import { JourneyBadges } from './CallCentreBadges';
import { JourneyRecordModal } from './JourneyRecordForm';
import { callCentreKeys } from './callCentreUtils';

interface MemberJourneyCardProps {
  memberId: number;
  memberName?: string | null;
}

export function MemberJourneyCard({ memberId, memberName }: MemberJourneyCardProps) {
  const { hasPermission, hasRole } = useEnrichedAuth();
  const perms = useCallCentrePermissions();
  const allowed = perms.canRead || hasPermission('Member.Read') || hasRole('Admin');
  const admin = useCallCentreAdminService();
  const [editing, setEditing] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: callCentreKeys.memberJourney(memberId),
    queryFn: () => admin.getMemberJourney(memberId),
    enabled: allowed && memberId > 0,
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  if (!allowed || isError) return null;
  if (isLoading) return <Skeleton height={86} radius="md" />;
  if (!data || data.steps.length === 0) return null;

  const total = data.steps.length;

  return (
    <Card withBorder radius="md" padding="md" shadow="sm">
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon variant="light" color="teal" radius="md" size="lg">
              <IconRoute size={18} />
            </ThemeIcon>
            <Stack gap={0}>
              <Text fw={600}>Journey</Text>
              <Text size="xs" c="dimmed">
                {data.completedCount} of {total} steps completed
              </Text>
            </Stack>
          </Group>
          {perms.canManageJourney && (
            <Button size="compact-sm" variant="light" leftSection={<IconPencil size={14} />} onClick={() => setEditing(true)}>
              Edit
            </Button>
          )}
        </Group>
        <JourneyBadges steps={data.steps} showPco />
      </Stack>
      {perms.canManageJourney && <JourneyRecordModal opened={editing} onClose={() => setEditing(false)} target={{ memberId, name: memberName ?? null }} />}
    </Card>
  );
}
